import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IPaymentCard } from '../../types/global/generic.types';
import PaymentCard from '../PaymentCard/PaymentCard';
import { SimpleAddCardModal } from '../Payment/SimpleAddCardModal';
import { PayStack } from '../Payment/Paystack';
import { isEmpty } from '../../utils/lodash';
import { getJWtDetails, showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { initializeFlutterwaveBillingCard, getPaymentMethods } from '../../redux/payment/action';
import { en_config } from '../../config';
import { ROUTES } from '../../types/global/routes.types';
import { filterPaymentCardsForRail, SubscriptionPaymentRail } from '../../utils/subscriptionRailCards';
import { publishableKeySelector } from '../../pages/EsSettings/selector';
import { useDispatch } from 'react-redux';

const SURFACE = '#141414';
const OUTLINE = 'rgba(255,255,255,0.12)';
const PRIMARY = '#ffc965';

export interface CreatorUpdatePaymentModalProps {
    open: boolean;
    onClose: () => void;
    userCards?: IPaymentCard[];
    /** @deprecated Prefer `provider` */
    isNonUs?: boolean;
    /** Active billing rail for this subscription */
    provider?: SubscriptionPaymentRail;
    loading?: boolean;
    onConfirm: (card: IPaymentCard) => void;
    /** Optional: open provider-hosted update as fallback */
    onHostedUpdate?: () => void;
    hostedUpdating?: boolean;
}

export function CreatorUpdatePaymentModal({
    open,
    onClose,
    userCards,
    isNonUs,
    provider: providerProp,
    loading,
    onConfirm,
    onHostedUpdate,
    hostedUpdating,
}: CreatorUpdatePaymentModalProps) {
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const { user } = getJWtDetails();
    const { publishableKey } = useSelector(publishableKeySelector);
    const [selectedCard, setSelectedCard] = useState<IPaymentCard | undefined>();
    const [flutterwaveAdding, setFlutterwaveAdding] = useState(false);

    const provider: SubscriptionPaymentRail = providerProp ?? (isNonUs ? 'paystack' : 'stripe');
    const isHostedRail = provider === 'paystack' || provider === 'flutterwave';
    const providerLabel = provider === 'flutterwave' ? 'Flutterwave' : provider === 'paystack' ? 'Paystack' : 'Stripe';

    const railCards = useMemo(() => filterPaymentCardsForRail(userCards, provider), [userCards, provider]);

    useEffect(() => {
        if (!open) {
            setSelectedCard(undefined);
            return;
        }
        if (!railCards.length) {
            setSelectedCard(undefined);
            return;
        }
        setSelectedCard(railCards[0]);
    }, [open, railCards]);

    const handleClose = () => {
        if (loading || hostedUpdating || flutterwaveAdding) return;
        setSelectedCard(undefined);
        onClose();
    };

    const handleFlutterwaveAddCard = async () => {
        setFlutterwaveAdding(true);
        try {
            const res = (await dispatchPromise(
                initializeFlutterwaveBillingCard({
                    callback_url: `${window.location.origin}${ROUTES.ACCOUNT_SUBSCRIPTION}`,
                }),
            )) as any;
            const payload = res?.data ?? res;
            const authUrl = payload?.authorization_url || payload?.payment_link;
            if (!authUrl) {
                showToastMessage('Could not start Flutterwave card check. Try again.', 'error');
                return;
            }
            window.location.href = authUrl;
        } catch {
            showToastMessage('Could not start Flutterwave card check. Try again.', 'error');
        } finally {
            setFlutterwaveAdding(false);
        }
    };

    const canConfirm = isHostedRail ? Boolean(selectedCard?._id) : Boolean(selectedCard?.payment_method);

    const renderAddCard = () => {
        if (provider === 'stripe') {
            return (
                <Box sx={{ mt: 2 }}>
                    <SimpleAddCardModal variant="link" />
                </Box>
            );
        }
        if (provider === 'paystack' && user && publishableKey) {
            return (
                <Box sx={{ mt: 2 }}>
                    <PayStack
                        email={user.email}
                        amount={en_config.PAYSTACK_CHARGE}
                        publicKey={publishableKey}
                        isAddNewCardButton={false}
                        addButtonSx={{
                            border: 'none',
                            bgcolor: 'transparent',
                            color: PRIMARY,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            px: 0,
                            minWidth: 0,
                        }}
                        addButtonTextStyle={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: PRIMARY,
                        }}
                        addButtonProps={{ variant: 'text' }}
                    />
                </Box>
            );
        }
        if (provider === 'flutterwave') {
            return (
                <Box sx={{ mt: 2 }}>
                    <Button
                        type="button"
                        onClick={() => void handleFlutterwaveAddCard()}
                        disabled={flutterwaveAdding}
                        sx={{
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: PRIMARY,
                            px: 0,
                            minWidth: 0,
                        }}>
                        {flutterwaveAdding ? 'Opening…' : 'Add card'}
                    </Button>
                </Box>
            );
        }
        return null;
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{
                sx: {
                    bgcolor: SURFACE,
                    backgroundImage: 'none',
                    borderRadius: 3,
                    border: `1px solid ${OUTLINE}`,
                },
            }}>
            <DialogTitle sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff' }}>
                Update payment method
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', mb: 2, lineHeight: 1.5 }}>
                    Choose a saved card or add a new one. Your subscription billing will use this payment method.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {railCards.map(card => (
                        <Box
                            key={card._id}
                            onClick={() => setSelectedCard(card)}
                            sx={{
                                cursor: 'pointer',
                                borderRadius: 2,
                                border:
                                    selectedCard?._id === card._id ? `2px solid ${PRIMARY}` : `1px solid ${OUTLINE}`,
                                p: 0.5,
                            }}>
                            <PaymentCard card={card} />
                        </Box>
                    ))}
                </Box>
                {isEmpty(railCards) && (
                    <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', py: 2, textAlign: 'center' }}>
                        No {providerLabel} cards on file yet.
                    </Typography>
                )}
                {renderAddCard()}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexWrap: 'wrap' }}>
                <Button
                    onClick={handleClose}
                    disabled={loading || hostedUpdating}
                    sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.6)' }}>
                    Cancel
                </Button>
                {isHostedRail && onHostedUpdate ? (
                    <Button
                        onClick={onHostedUpdate}
                        disabled={loading || hostedUpdating}
                        startIcon={hostedUpdating ? <CircularProgress size={14} sx={{ color: PRIMARY }} /> : null}
                        sx={{ textTransform: 'none', color: PRIMARY }}>
                        {hostedUpdating ? `Opening ${providerLabel}…` : `Update on ${providerLabel}`}
                    </Button>
                ) : null}
                <Button
                    onClick={() => {
                        if (!selectedCard) return;
                        // After Paystack inline add, refresh cards list for next open
                        if (isHostedRail) dispatch(getPaymentMethods());
                        onConfirm(selectedCard);
                    }}
                    disabled={!canConfirm || loading || hostedUpdating}
                    startIcon={loading ? <CircularProgress size={14} sx={{ color: '#392700' }} /> : null}
                    sx={{
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: '#392700',
                        bgcolor: PRIMARY,
                        px: 2.5,
                        '&:hover': { bgcolor: '#feb700' },
                        '&.Mui-disabled': { opacity: 0.5, color: '#392700', bgcolor: PRIMARY },
                    }}>
                    {loading ? 'Updating…' : 'Save payment method'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
